import type { MedplumClient, WithId } from '@medplum/core';
import type { Bundle, CarePlan, PlanDefinition } from '@medplum/fhirtypes';

const PAGE_SIZE = 200;
// Belt-and-suspenders page cap so the `next`-link loop terminates even on a
// misbehaving server. A patient has at most a handful of care plans.
const MAX_PAGES = 50;

/**
 * Every CarePlan for one patient, fetched from Medplum FHIR by subject. The
 * patient's FHIR id (not the Eluve patient id) is required — resolve it via
 * `useMedplumPatientAccess` first. Sorted active-first, then server's
 * most-recent ordering.
 */
export async function fetchPatientCarePlans(
  client: MedplumClient,
  fhirPatientId: string,
): Promise<WithId<CarePlan>[]> {
  const results: WithId<CarePlan>[] = [];

  const ingest = (bundle: Bundle<CarePlan>) => {
    for (const entry of bundle.entry ?? []) {
      const r = entry.resource;
      if (r?.id) results.push(r as WithId<CarePlan>);
    }
    return bundle.link?.find((l) => l.relation === 'next')?.url;
  };

  let nextUrl = ingest(
    (await client.search('CarePlan', {
      subject: `Patient/${fhirPatientId}`,
      _count: PAGE_SIZE,
      _sort: '-_lastUpdated',
    })) as Bundle<CarePlan>,
  );
  let pages = 1;
  while (nextUrl && pages < MAX_PAGES) {
    const bundle = (await client.get(nextUrl)) as Bundle<CarePlan>;
    nextUrl = ingest(bundle);
    pages += 1;
  }

  const rank = (s: CarePlan['status']) => (s === 'active' ? 0 : 1);
  results.sort((a, b) => rank(a.status) - rank(b.status));
  return results;
}

// --- Display helpers (pure) ------------------------------------------------

/**
 * A patient-facing title for a CarePlan. The FHIR CarePlan has no title of its
 * own, so we lean on the first category that carries display text, then fall
 * back to the instantiated protocol's canonical slug.
 */
export function carePlanPlanTitle(carePlan: CarePlan): string {
  const categoryWithText = carePlan.category?.find((c) => c.text);
  if (categoryWithText?.text) return categoryWithText.text;

  const canonical = carePlan.instantiatesCanonical?.[0];
  if (canonical) {
    const slug = canonical.split('|')[0]?.split('/').pop();
    if (slug) return slug;
  }
  return 'Care plan';
}

export function planDefinitionTitle(pd: PlanDefinition): string {
  return pd.title ?? pd.name ?? 'Untitled plan';
}

/** The canonical reference a CarePlan instantiates: `{url}|{version}`. */
export function planDefinitionCanonical(
  pd: PlanDefinition,
): string | undefined {
  if (!pd.url) return undefined;
  return pd.version ? `${pd.url}|${pd.version}` : pd.url;
}

/** Conditions a protocol addresses, pulled from PlanDefinition.topic codings. */
export function planConditions(pd: PlanDefinition): string[] {
  const out: string[] = [];
  for (const topic of pd.topic ?? []) {
    for (const coding of topic.coding ?? []) {
      if (coding.display) out.push(coding.display);
    }
    if (!topic.coding?.length && topic.text) out.push(topic.text);
  }
  return out;
}

export interface CarePlanActivitySummary {
  title: string;
  status: string | undefined;
  description: string | undefined;
  start: string | undefined;
  end: string | undefined;
}

/**
 * A flat, display-ready view of a CarePlan's activities for the detail list.
 * Reads `activity.detail` generically (code text/display, status, scheduled
 * period) without assuming any tenant-specific coding system. Reference-only
 * activities (`activity.reference` with no inline `detail`) surface the
 * reference's display text; resolving the referenced resource is out of scope
 * here.
 */
export function carePlanActivitySummaries(
  carePlan: CarePlan,
): CarePlanActivitySummary[] {
  return (carePlan.activity ?? []).map((activity) => {
    const detail = activity.detail;
    const codeText =
      detail?.code?.text ??
      detail?.code?.coding?.find((c) => c.display)?.display;
    return {
      title: codeText ?? activity.reference?.display ?? 'Activity',
      status: detail?.status,
      description: detail?.description,
      start: detail?.scheduledPeriod?.start,
      end: detail?.scheduledPeriod?.end,
    };
  });
}
